import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Save, KeyRound, Server } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    lm_studio_url: "",
    lm_studio_model: "",
    ollama_url: "",
    ollama_model: "",
    ollama_cloud_api_key: "",
    ollama_cloud_model: "",
    default_provider: "ollama",
  });
  const [masked, setMasked] = useState("");
  const [saved, setSaved] = useState(false);

  const load = async () => {
    const { data } = await api.get("/ai/settings");
    setForm({
      lm_studio_url: data.lm_studio_url || "",
      lm_studio_model: data.lm_studio_model || "",
      ollama_url: data.ollama_url || "",
      ollama_model: data.ollama_model || "",
      ollama_cloud_api_key: "",
      ollama_cloud_model: data.ollama_cloud_model || "",
      default_provider: data.default_provider || "ollama",
    });
    setMasked(data.ollama_cloud_api_key_masked || "");
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.ollama_cloud_api_key) delete payload.ollama_cloud_api_key;
    await api.put("/ai/settings", payload);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
    load();
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <header className="mb-8">
        <div className="label-mono text-green-400 mb-2">// config.suite</div>
        <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter" data-testid="settings-title">Settings</h1>
        <p className="text-neutral-400 text-sm mt-2">Wire up your AI providers. Endpoints stay on your machine.</p>
      </header>

      <section className="panel p-6 mb-6" data-testid="settings-profile">
        <div className="label-mono mb-4">// profile</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="label-mono mb-1">Name</div>
            <div className="font-jet">{user?.name}</div>
          </div>
          <div>
            <div className="label-mono mb-1">Email</div>
            <div className="font-jet">{user?.email}</div>
          </div>
          <div>
            <div className="label-mono mb-1">Role</div>
            <div className="font-jet text-amber-400">{user?.role}</div>
          </div>
        </div>
      </section>

      <form onSubmit={save} data-testid="settings-ai-form" className="panel panel-accent-red p-6">
        <div className="flex items-center gap-3 mb-5">
          <Server size={14} className="text-green-400" />
          <div className="label-mono">// ai.providers</div>
        </div>

        <div className="mb-6">
          <span className="label-mono block mb-2">Default Provider</span>
          <select data-testid="settings-default-provider" value={form.default_provider} onChange={(e) => setForm({ ...form, default_provider: e.target.value })} className="input-shell w-full md:w-72 px-3 py-2 text-sm font-jet">
            <option value="ollama">Ollama (local)</option>
            <option value="lm_studio">LM Studio (local)</option>
            <option value="ollama_cloud">Ollama Cloud</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="panel p-4">
            <div className="font-serif text-lg mb-3">Ollama (local)</div>
            <label className="block mb-3">
              <span className="label-mono block mb-1">Base URL</span>
              <input data-testid="settings-ollama-url" value={form.ollama_url} onChange={(e) => setForm({ ...form, ollama_url: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" placeholder="http://localhost:11434" />
            </label>
            <label className="block">
              <span className="label-mono block mb-1">Default Model</span>
              <input data-testid="settings-ollama-model" value={form.ollama_model} onChange={(e) => setForm({ ...form, ollama_model: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" placeholder="llama3.1" />
            </label>
          </div>

          <div className="panel p-4">
            <div className="font-serif text-lg mb-3">LM Studio</div>
            <label className="block mb-3">
              <span className="label-mono block mb-1">OpenAI-compatible Base</span>
              <input data-testid="settings-lmstudio-url" value={form.lm_studio_url} onChange={(e) => setForm({ ...form, lm_studio_url: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" placeholder="http://localhost:1234/v1" />
            </label>
            <label className="block">
              <span className="label-mono block mb-1">Default Model</span>
              <input data-testid="settings-lmstudio-model" value={form.lm_studio_model} onChange={(e) => setForm({ ...form, lm_studio_model: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" placeholder="local-model" />
            </label>
          </div>

          <div className="panel p-4 md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={14} className="text-amber-400" />
              <div className="font-serif text-lg">Ollama Cloud</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="label-mono block mb-1">API Key {masked && <span className="text-amber-400 ml-2">{masked}</span>}</span>
                <input data-testid="settings-cloud-key" type="password" value={form.ollama_cloud_api_key} onChange={(e) => setForm({ ...form, ollama_cloud_api_key: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" placeholder={masked ? "•••• keep existing" : "ollama_cloud_api_key"} />
              </label>
              <label className="block">
                <span className="label-mono block mb-1">Default Model</span>
                <input data-testid="settings-cloud-model" value={form.ollama_cloud_model} onChange={(e) => setForm({ ...form, ollama_cloud_model: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" placeholder="llama3.1:70b" />
              </label>
            </div>
            <p className="text-[11px] text-neutral-500 mt-3 font-jet">
              Get a key from your Ollama account. Never stored in plaintext on the client.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button type="submit" data-testid="settings-save-button" className="btn-primary btn-glitch flex items-center gap-2">
            <Save size={14} /> Save settings
          </button>
          {saved && <span className="label-mono text-green-400" data-testid="settings-saved">// saved</span>}
        </div>
      </form>
    </div>
  );
}
